type RootLike = HTMLElement | Document;

export interface ProgressiveListOptions<T> {
  container: HTMLElement;
  items: T[];
  renderItem: (item: T) => HTMLElement;
  skeletonClassName: string;
  batchSize?: number;
  skeletonCount?: number;
  endMessage?: string;
  endElement?: () => HTMLElement;
  emptyElement?: HTMLElement | null;
  root?: RootLike;
  rootMargin?: string;
  /**
   * Render the first batch synchronously, without the skeleton flash
   * or the artificial 90ms delay. Use this when the items are already
   * in memory (e.g. switching a browse/AI-create filter): the data is
   * there instantly so a skeleton would only introduce a flicker.
   * Later infinite-scroll batches still use the skeleton path.
   */
  immediate?: boolean;
}

interface ProgressiveState {
  observer?: IntersectionObserver;
  token: number;
  loading: boolean;
  cursor: number;
	ended: boolean;
	skeletonClassName?: string;
	paged?: PagedListController;
	cleanupUserIntent?: () => void;
}

interface PagedListWaiter {
	predicate: (items: unknown[]) => boolean;
	resolve: (matched: boolean) => void;
}

interface PagedListController {
	items: unknown[];
	loadNext: () => void;
	waiters: PagedListWaiter[];
}

const states = new WeakMap<HTMLElement, ProgressiveState>();
const RETURN_RESERVE_ATTRIBUTE = 'data-return-reserve';

export interface PagedInfiniteListOptions<T> {
  container: HTMLElement;
  loadPage: (cursor: string | null) => Promise<{ items: T[]; hasMore?: boolean; nextCursor?: string | null }>;
  renderItem: (item: T) => HTMLElement;
  skeletonClassName: string;
  skeletonCount?: number;
  endMessage?: string;
  endElement?: () => HTMLElement;
  emptyElement?: HTMLElement | null;
  root?: RootLike;
  rootMargin?: string;
  onItemsChange?: (items: T[]) => void;
  onPage?: (page: { items: T[]; hasMore?: boolean; nextCursor?: string | null }) => void;
  renderError?: (error: unknown, retryLoad: () => void) => HTMLElement | null;
	/** Do not let an immediately visible sentinel prefetch page two before a real scroll/touch/key intent. */
	requireUserIntentForNextPage?: boolean;
	/** Fired exactly when a non-initial page starts loading. */
	onNonInitialPageLoad?: () => void;
  initialPage?: {
    items: T[];
    nextCursor?: string | null;
    hasMore?: boolean;
		/** Re-register a retained page without replacing its already-painted cards. */
		preserveExistingDom?: boolean;
		/** A breakpoint rebind may switch a retained pager to desktop auto-observe. */
		preserveExistingDomImmediate?: boolean;
  };
}

export function renderProgressiveList<T>(options: ProgressiveListOptions<T>): void {
  const previous = states.get(options.container);
  previous?.observer?.disconnect();

  const state: ProgressiveState = {
    token: Date.now() + Math.random(),
    loading: false,
    cursor: 0,
	ended: false,
		skeletonClassName: options.skeletonClassName,
  };
  states.set(options.container, state);
	options.container.replaceChildren();

  if (options.items.length === 0) {
    if (options.emptyElement) options.container.appendChild(options.emptyElement);
    return;
  }

  const commitBatch = () => {
    // First batch with immediate=true: render the entire items list
    // in one synchronous pass. The `immediate` contract is "items
    // are already in memory" (browse-grid cache, AI-create cache,
    // home trending pre-fetch, filter switch), so splitting the
    // first paint into `batchSize` chunks only added an artificial
    // second-batch scroll requirement: e.g. browse grid (18 topics,
    // batch=8) was painting 16 first, then a scroll-triggered
    // IntersectionObserver batch surfaced the last 2 — the user
    // would have to scroll to discover that browse had more
    // content. Now the first paint is the canonical count and the
    // observer is wired up after, ready for any later dynamic
    // growth (e.g. AI-create, where more courses can arrive while
    // the user is on the page).
    //
    // If items.length is very large (>500), we still cap to
    // batchSize on the immediate first paint to keep the main
    // thread responsive; the threshold is high enough that every
    // current caller renders everything.
    const LARGE = 500;
    const cap = options.immediate && state.cursor === 0 && options.items.length <= LARGE
      ? options.items.length
      : (options.batchSize ?? 8);
    const next = options.items.slice(state.cursor, state.cursor + cap);
    state.cursor += next.length;
    for (const item of next) {
      const node = options.renderItem(item);
		applyListEnterAnimation(node);
      options.container.appendChild(node);
    }
    state.loading = false;
    if (state.cursor >= options.items.length) {
      endList(options, state);
    } else {
      observeSentinel(options, state, loadNext);
    }
  };

  const loadNext = () => {
    const current = states.get(options.container);
    if (!current || current.token !== state.token) return;
    if (state.loading || state.ended) return;
    if (state.cursor >= options.items.length) {
      endList(options, state);
      return;
    }
    state.loading = true;
    removeSentinel(options.container);
    // First batch with immediate=true: render synchronously in the
    // same task so the browser never paints the empty/skeleton frame.
    if (state.cursor === 0 && options.immediate) {
      commitBatch();
      return;
    }
    const skeletons = appendSkeletons(options);
    window.setTimeout(() => {
      const current = states.get(options.container);
      if (!current || current.token !== state.token) return;
      for (const skeleton of skeletons) skeleton.remove();
      commitBatch();
    }, state.cursor === 0 ? 90 : 180);
  };

  loadNext();
}

export function renderPagedInfiniteList<T>(options: PagedInfiniteListOptions<T>): void {
	const previous = states.get(options.container);
	previous?.observer?.disconnect();
	previous?.cleanupUserIntent?.();

  const state: ProgressiveState = {
    token: Date.now() + Math.random(),
    loading: false,
    cursor: 0,
    ended: false,
		skeletonClassName: options.skeletonClassName,
  };
	states.set(options.container, state);
	if (!options.initialPage?.preserveExistingDom) options.container.replaceChildren();

  const loaded: T[] = [];
  let nextCursor: string | null = options.initialPage?.nextCursor ?? null;

  const asProgressiveOptions = (items: T[]): ProgressiveListOptions<T> => ({
    container: options.container,
    items,
    renderItem: options.renderItem,
    skeletonClassName: options.skeletonClassName,
    skeletonCount: options.skeletonCount,
    endMessage: options.endMessage,
    endElement: options.endElement,
    emptyElement: options.emptyElement,
    root: options.root,
    rootMargin: options.rootMargin,
  });

  const loadNext = () => {
    const current = states.get(options.container);
    if (!current || current.token !== state.token) return;
    if (state.loading || state.ended) return;
		if (loaded.length > 0) options.onNonInitialPageLoad?.();
    state.loading = true;
    removeSentinel(options.container);
    const skeletonItems = loaded.length ? loaded : Array.from({ length: options.skeletonCount ?? 12 }, () => null as T);
    const listOptions = asProgressiveOptions(skeletonItems);
		// A Home Back can retain an interrupted next-page skeleton as an invisible
		// footprint. Reuse it only after a real user intent has resumed this
		// controller: removing it first would shrink the document and clamp the
		// return anchor, while appending a second set would make the grid jump.
		const skeletons = takeReturnReserves(options.container, options.skeletonClassName);
		if (skeletons.length === 0) skeletons.push(...appendSkeletons(listOptions));
    void options.loadPage(nextCursor).then((page) => {
      const current = states.get(options.container);
      if (!current || current.token !== state.token) return;
      for (const skeleton of skeletons) skeleton.remove();
      const items = page.items ?? [];
      if (loaded.length === 0 && items.length === 0) {
			options.onItemsChange?.([]);
        if (options.emptyElement) options.container.appendChild(options.emptyElement);
        state.loading = false;
        state.ended = true;
        state.observer?.disconnect();
        removeSentinel(options.container);
			settlePagedWaiters(options.container, state);
        return;
      }
      // Paged lists can prefetch more content before it is in view. Applying
      // an entrance motion to any fetched page makes that automatic work look
      // like a second whole-list render, so all paged items paint final-state.
      loaded.push(...items);
      options.onPage?.(page);
      options.onItemsChange?.(loaded.slice());
		for (const item of items) {
			const node = options.renderItem(item);
			options.container.appendChild(node);
		}
      nextCursor = page.nextCursor ?? null;
      state.loading = false;
      if (page.hasMore && nextCursor) {
        if (options.requireUserIntentForNextPage) observeSentinelAfterUserIntent(asProgressiveOptions(loaded), state, loadNext);
        else observeSentinel(asProgressiveOptions(loaded), state, loadNext);
      } else {
        endList(listOptions, state);
      }
			settlePagedWaiters(options.container, state);
    }).catch((error: unknown) => {
      const current = states.get(options.container);
      if (!current || current.token !== state.token) return;
      for (const skeleton of skeletons) skeleton.remove();
      state.loading = false;
      state.ended = true;
      state.observer?.disconnect();
      removeSentinel(options.container);
			settlePagedWaiters(options.container, state);
      if (loaded.length === 0) {
        const retryLoad = () => {
          const retryState = states.get(options.container);
          if (!retryState || retryState.token !== state.token) return;
          options.container.replaceChildren();
          state.ended = false;
          loadNext();
        };
        const errorElement = options.renderError?.(error, retryLoad) ?? renderPagedListError(retryLoad);
        options.container.replaceChildren(errorElement);
        return;
      }
      const errorElement = renderPagedListError(() => {
        const retryState = states.get(options.container);
        if (!retryState || retryState.token !== state.token) return;
        errorElement.remove();
        state.ended = false;
        loadNext();
      }, true);
      options.container.appendChild(errorElement);
    });
  };
	state.paged = { items: loaded, loadNext, waiters: [] };

	if (options.initialPage) {
		const page = options.initialPage;
		const items = page.items ?? [];
		if (items.length === 0) {
			clearReturnReserves(options.container);
			options.onItemsChange?.([]);
			if (options.emptyElement) options.container.appendChild(options.emptyElement);
			state.ended = true;
			settlePagedWaiters(options.container, state);
			return;
		}
		loaded.push(...items);
		options.onPage?.(page);
		options.onItemsChange?.(loaded.slice());
		if (!page.preserveExistingDom) {
			for (const item of items) {
				const node = options.renderItem(item);
				options.container.appendChild(node);
			}
		}
		if (page.hasMore && nextCursor) {
			// A retained Home pager follows the mode it is restored into: desktop
			// may observe immediately, while mobile still waits for real intent.
			if ((page.preserveExistingDom && !page.preserveExistingDomImmediate) || options.requireUserIntentForNextPage) observeSentinelAfterUserIntent(asProgressiveOptions(loaded), state, loadNext);
			else observeSentinel(asProgressiveOptions(loaded), state, loadNext);
			} else if (page.preserveExistingDom) {
				clearReturnReserves(options.container);
				state.ended = true;
			state.observer?.disconnect();
		} else {
			endList(asProgressiveOptions(loaded), state);
		}
		settlePagedWaiters(options.container, state);
		return;
	}

  loadNext();
}

/**
 * Ensures a paged list has rendered an item needed by a contextual return.
 * This deliberately resumes through the existing pager rather than copying
 * card DOM into history, so normal dedupe/error/terminal behaviour remains
 * the single source of truth.
 */
export function loadPagedInfiniteListUntil<T>(container: HTMLElement, predicate: (items: T[]) => boolean): Promise<boolean> {
	const state = states.get(container);
	const paged = state?.paged;
	if (!state || !paged) return Promise.resolve(false);
	const typedPredicate = predicate as (items: unknown[]) => boolean;
	if (typedPredicate(paged.items)) return Promise.resolve(true);
	if (state.ended) return Promise.resolve(false);
	return new Promise((resolve) => {
		paged.waiters.push({ predicate: typedPredicate, resolve });
		if (!state.loading) paged.loadNext();
	});
}

/** Cancels an active paged stream before replacing it with a new pagination session. */
export function clearPagedInfiniteList(container: HTMLElement): void {
	const previous = states.get(container);
	if (previous) {
		previous.ended = true;
		previous.loading = false;
		previous.observer?.disconnect();
		previous.cleanupUserIntent?.();
		for (const waiter of previous.paged?.waiters ?? []) waiter.resolve(false);
		if (previous.paged) previous.paged.waiters = [];
	}
	states.set(container, { token: Date.now() + Math.random(), loading: false, cursor: 0, ended: true });
	container.replaceChildren();
}

/**
 * Makes every completion owned by the current paged controller a no-op while
 * deliberately retaining its DOM. Navigation uses this for a hidden Browse
 * view: a late first-page completion must not rewrite the preserved return
 * frame, but replacing/removing nodes here would create the same visual jump.
 */
export function invalidatePagedInfiniteList(container: HTMLElement, options: { preserveReturnReserve?: boolean } = {}): void {
	const previous = states.get(container);
	if (!previous) return;
	previous.ended = true;
	previous.loading = false;
	previous.observer?.disconnect();
	previous.cleanupUserIntent?.();
	for (const waiter of previous.paged?.waiters ?? []) waiter.resolve(false);
	if (previous.paged) previous.paged.waiters = [];
	states.set(container, {
		token: Date.now() + Math.random(),
		loading: false,
		cursor: 0,
		ended: true,
		skeletonClassName: previous.skeletonClassName,
	});
	if (!options.preserveReturnReserve) {
		if (previous.skeletonClassName) for (const node of skeletonNodes(container, previous.skeletonClassName)) node.remove();
		clearReturnReserves(container);
	}
}

/** True only while a paged controller has an actual page request in flight. */
export function isPagedInfiniteListLoading(container: HTMLElement): boolean {
	const state = states.get(container);
	return state?.paged !== undefined && state.paged.items.length > 0 && state.loading === true;
}

/** Pauses a retained rendered page so a later return can install a fresh controller. */
export function suspendPagedInfiniteList(container: HTMLElement): void {
	const previous = states.get(container);
	if (!previous) return;
	previous.ended = true;
	previous.loading = false;
	previous.observer?.disconnect();
	previous.cleanupUserIntent?.();
	for (const waiter of previous.paged?.waiters ?? []) waiter.resolve(false);
	if (previous.paged) previous.paged.waiters = [];
	// Replace the registered token immediately. A pending page's then/catch
	// checks this state before touching DOM, so it cannot mutate a retained
	// Home list after navigation has left the view.
	states.set(container, {
		token: Date.now() + Math.random(),
		loading: false,
		cursor: 0,
		ended: true,
		skeletonClassName: previous.skeletonClassName,
	});
	if (previous.skeletonClassName) {
		for (const node of skeletonNodes(container, previous.skeletonClassName)) {
			node.setAttribute(RETURN_RESERVE_ATTRIBUTE, 'true');
			node.setAttribute('aria-hidden', 'true');
			node.setAttribute('inert', '');
		}
	}
}

/** Removes a completed/interrupted entrance class from retained DOM before it becomes visible again. */
export function clearListEnterAnimations(container: HTMLElement): void {
	for (const node of Array.from(container.querySelectorAll<HTMLElement>('.dmx-list-enter'))) node.classList.remove('dmx-list-enter');
}

function applyListEnterAnimation(node: HTMLElement): void {
	node.classList.add('dmx-list-enter');
	if (typeof node.addEventListener !== 'function') return;
	const clear = () => node.classList.remove('dmx-list-enter');
	node.addEventListener('animationend', clear, { once: true });
	// Reduced-motion disables animation and interrupted display changes can skip
	// animationend. Keep the class strictly transient in both cases.
	globalThis.setTimeout(clear, 400);
}

function settlePagedWaiters(container: HTMLElement, state: ProgressiveState): void {
	const paged = state.paged;
	if (!paged || paged.waiters.length === 0) return;
	const pending: PagedListWaiter[] = [];
	for (const waiter of paged.waiters) {
		if (waiter.predicate(paged.items)) waiter.resolve(true);
		else if (state.ended) waiter.resolve(false);
		else pending.push(waiter);
	}
	paged.waiters = pending;
	if (pending.length > 0 && !state.loading && !state.ended) paged.loadNext();
	void container;
}

function renderPagedListError(retryLoad: () => void, appendToLoadedList = false): HTMLElement {
	const error = document.createElement('div');
	error.className = appendToLoadedList ? 'infinite-list-page-error' : 'infinite-list-error';
	error.setAttribute('role', 'alert');
	const message = document.createElement('p');
	message.textContent = "Couldn't load this list. Please try again.";
	const retry = document.createElement('button');
	retry.type = 'button';
	retry.className = 'btn btn-primary btn-sm';
	retry.textContent = 'Retry';
	retry.addEventListener('click', retryLoad);
	error.appendChild(message);
	error.appendChild(retry);
	return error;
}

function appendSkeletons<T>(options: ProgressiveListOptions<T>): HTMLElement[] {
  const remaining = Math.max(1, Math.min(options.skeletonCount ?? 3, options.items.length));
  const skeletons = Array.from({ length: remaining }, () => {
    const node = document.createElement("div");
    node.className = options.skeletonClassName;
    node.setAttribute("aria-hidden", "true");
    return node;
  });
  for (const skeleton of skeletons) options.container.appendChild(skeleton);
  return skeletons;
}

function takeReturnReserves(container: HTMLElement, skeletonClassName: string): HTMLElement[] {
	const reserves = skeletonNodes(container, skeletonClassName).filter((node) => node.hasAttribute(RETURN_RESERVE_ATTRIBUTE));
	for (const reserve of reserves) {
		reserve.removeAttribute(RETURN_RESERVE_ATTRIBUTE);
		reserve.removeAttribute('inert');
	}
	return reserves;
}

function clearReturnReserves(container: HTMLElement): void {
	if (typeof container.querySelectorAll !== 'function') return;
	for (const node of Array.from(container.querySelectorAll<HTMLElement>(`[${RETURN_RESERVE_ATTRIBUTE}]`))) node.remove();
}

function skeletonNodes(container: HTMLElement, className: string): HTMLElement[] {
	if (typeof container.querySelectorAll !== 'function') return [];
	return Array.from(container.querySelectorAll<HTMLElement>(`.${className}`));
}

function observeSentinel<T>(
  options: ProgressiveListOptions<T>,
  state: ProgressiveState,
  loadNext: () => void,
): void {
  let sentinel = options.container.querySelector<HTMLElement>('.infinite-list-sentinel');
  if (!sentinel) {
    sentinel = document.createElement("div");
    sentinel.className = "infinite-list-sentinel";
    sentinel.setAttribute("aria-hidden", "true");
    options.container.appendChild(sentinel);
  }
  if (!("IntersectionObserver" in window)) {
    globalThis.setTimeout(loadNext, 120);
    return;
  }
  state.observer?.disconnect();
  state.observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) loadNext();
  }, {
    root: scrollRoot(options.root),
    rootMargin: options.rootMargin ?? "720px 0px 720px 0px",
    threshold: 0,
  });
  state.observer.observe(sentinel);
}

function observeSentinelAfterUserIntent<T>(options: ProgressiveListOptions<T>, state: ProgressiveState, loadNext: () => void): void {
	const view = options.container.ownerDocument?.defaultView;
	if (!view) {
		observeSentinel(options, state, loadNext);
		return;
	}
	const activate = () => {
		state.cleanupUserIntent?.();
		state.cleanupUserIntent = undefined;
		const current = states.get(options.container);
		if (!current || current.token !== state.token || state.ended) return;
		observeSentinel(options, state, loadNext);
	};
	const activateAfterIntent = (event: Event) => {
		if (event.type === 'keydown' && !isScrollableKeyboardIntent(event as KeyboardEvent)) return;
		if (!event.isTrusted && !isJsdomWindow(view)) return;
		activate();
	};
	const events: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
	const cleanup = () => events.forEach((event) => view.removeEventListener(event, activateAfterIntent));
	state.cleanupUserIntent = cleanup;
	// A filtered key (typing Space, Tab, or a modified shortcut) must not
	// consume the deferred listener. activate() performs the one-time cleanup.
	events.forEach((event) => view.addEventListener(event, activateAfterIntent, { passive: true }));
}

function isScrollableKeyboardIntent(event: KeyboardEvent): boolean {
	if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return false;
	if (!['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', ' ', 'Spacebar', 'End', 'Home'].includes(event.key)) return false;
	const target = event.target;
	if (!target || typeof (target as Element).matches !== 'function') return true;
	const element = target as Element & { isContentEditable?: boolean };
	return !element.matches('input, textarea, select, [contenteditable], [contenteditable="true"]') && !element.isContentEditable;
}

function isJsdomWindow(view: Window): boolean {
	return /jsdom/i.test(view.navigator.userAgent);
}

function endList<T>(options: ProgressiveListOptions<T>, state: ProgressiveState): void {
  if (state.ended) return;
  state.ended = true;
	state.observer?.disconnect();
	clearReturnReserves(options.container);
  removeSentinel(options.container);
  const end = options.endElement?.() ?? document.createElement("div");
  end.classList.add("infinite-list-end");
  if (!options.endElement) end.textContent = options.endMessage ?? "You're all caught up.";
  options.container.appendChild(end);
}

function removeSentinel(container: HTMLElement): void {
  container.querySelector(".infinite-list-sentinel")?.remove();
}

function scrollRoot(root: RootLike | undefined): Element | Document | null {
  if (!root || root instanceof Document) return null;
  return root;
}
